<template>
  <div>
    <div class="input-container m-t-0" :class="{ 'bd-b-gray30': inline }" static>
      <vk-icon v-if="inline" name="pin" class="LOCATION-ICON" />
      <input
          :disabled="disabled"
          ref="location"
          placeholder="Street address"
          class="input ellipsize p-r-6"
          :class="{ 'INLINE': inline, 'p-l-6': inline }"
          type="text"
          @input="onAddress1Input"
          @keypress.enter.prevent
      />
      <a
          class="ICON"
          :class="{ 'DISABLED': disabled || !$options.location(this.value) }"
          @click.prevent="onClearClick">
        <vk-icon name="close-circle-filled" class="h4" />
      </a>
    </div>
    <div class="input-container" :class="{ 'm-t-2': !inline, 'm-t-0': inline }" static>
      <input
          :value="address_line_2"
          :disabled="!value.address_line_1 || disabled"
          placeholder="Apt, Suite (optional)"
          class="input ellipsize"
          :class="{ 'INLINE': inline }"
          type="text"
          @input="onAddress2Input"
      />
    </div>
  </div>
</template>

<script>
import location from '@/filters/location';

const PLACE_TYPES = {
  STREET_NUMBER: 'street_number',
  ROUTE: 'route',
  LOCALITY: 'locality',
  A_A_L_1: 'administrative_area_level_1',
  POSTAL_CODE: 'postal_code',
};
const PLACE_TYPES_TO_ADDRESS = {
  [PLACE_TYPES.STREET_NUMBER]: ['address_line_1', 'short_name'],
  [PLACE_TYPES.ROUTE]: ['address_line_1', 'long_name'],
  [PLACE_TYPES.LOCALITY]: ['city', 'long_name'],
  [PLACE_TYPES.A_A_L_1]: ['state', 'short_name'],
  [PLACE_TYPES.POSTAL_CODE]: ['zipcode', 'short_name'],
};

const emptyAddress = {
  address_line_1: '',
  address_line_2: '',
  city: '',
  state: '',
  zipcode: '',
};

export default {
  location,
  name: 'location-input',
  model: {
    prop: 'value',
    event: 'change',
  },
  props: {
    value: {
      type: Object,
      default: () => emptyAddress,
    },
    disabled: Boolean,
    inline: Boolean,
  },
  data () {
    return {
      address_line_2: this.value.address_line_2,
      autocomplete: null,
      autocompleteIdentifier: null,
    };
  },
  watch: {
    value () {
      this.$refs.location.value = location(this.value);

      if (this.value.address_line_1) {
        this.address_line_2 = this.value.address_line_2;
      }
    },
  },
  mounted () {
    this.$refs.location.value = location(this.value);
    this.autocomplete = new global.google.maps.places.Autocomplete(
      this.$refs.location,
      {
        fields: ['address_components'],
        types: ['address'],
        componentRestrictions: { country: ['us', 'pr', 'vi', 'gu', 'mp'] },
      }
    );
    this.autocompleteIdentifier = this.autocomplete.addListener(
      'place_changed',
      this.onPlaceChanged
    );
  },
  destroyed () {
    this.autocompleteIdentifier.remove();
  },
  methods: {
    onChange (address) {
      this.$emit('change', address ? { ...emptyAddress, ...address } : address);
    },
    onAddress1Input ({ target }) {
      if (target.value) {
        this.onChange({
          address_line_1: target.value,
          address_line_2: this.address_line_2,
        });
      } else {
        this.onChange();
      }
    },
    onAddress2Input ({ target }) {
      this.onChange({
        ...this.value,
        address_line_2: target.value,
      });
    },
    onClearClick () {
      this.address_line_2 = '';

      this.onChange();
    },
    onPlaceChanged () {
      const { address_components } = this.autocomplete.getPlace();

      if (!address_components) {
        return;
      }

      const address = address_components.reduce((acc, { types: [type], ...component }) => {
        const mappingKeys = PLACE_TYPES_TO_ADDRESS[type];
        if (mappingKeys) {
          const [addressKey, componentKey] = mappingKeys;
          let value = component[componentKey];

          switch (true) {
            case !acc[addressKey]:
              break;
            case type === PLACE_TYPES.STREET_NUMBER:
              value = `${value} ${acc[addressKey]}`;

              break;
            default:
              value = `${acc[addressKey]} ${value}`;
          }

          return {
            ...acc,
            [addressKey]: value,
          };
        }

        return acc;
      }, {});

      this.onChange({
        ...address,
        address_line_2: this.address_line_2,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~@homeservices/design-kit/tools";

  .ICON {
    @include absolute(50%, 2*$unit, auto, auto);
    @include square(3*$unit);
    @include link-black;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-gray40;
    transform: translateY(-50%);

    &.DISABLED {
      pointer-events: none;
    }
  }

  .LOCATION-ICON {
    @include absolute(50%, auto, auto, 2*$unit);
    @include h4;
    color: $color-gray80;
    transform: translateY(-50%);
  }

  .INLINE {
    border: none;
    margin-top: $unit/2;
    margin-bottom: $unit/2;
  }
</style>
